import type {
  StageCreateInDto,
  StageUpdateInDto,
  WorkflowStageOutDto,
} from 'shared/domain/workflow/types';
import { sortedStagesByFractionalOrder } from 'shared/domain/workflow';
import type { WorkflowSaveDiff, WorkflowPersistExecutors } from '../types';
import { planReorderOperations, buildReorderPayload } from './reorder';

export type WorkflowSavePhase =
  | 'updateWorkflow'
  | 'deleteStages'
  | 'updateStages'
  | 'createStages'
  | 'reorderStages';

export class WorkflowSavePhaseError extends Error {
  readonly phase: WorkflowSavePhase;
  readonly cause: unknown;

  constructor(phase: WorkflowSavePhase, cause: unknown) {
    const message =
      cause instanceof Error ? cause.message : String(cause ?? 'unknown error');
    super(`workflow save failed in phase "${phase}": ${message}`);
    this.name = 'WorkflowSavePhaseError';
    this.phase = phase;
    this.cause = cause;
  }
}

export type RunWorkflowSaveParams = {
  workflowId: string;
  projectId: string;
  diff: WorkflowSaveDiff;
  executor: WorkflowPersistExecutors;
  getStages: () => WorkflowStageOutDto[];
};

type WorkflowSaveContext = {
  workflowId: string;
  projectId: string;
  diff: WorkflowSaveDiff;
  executor: WorkflowPersistExecutors;
  getStages: () => WorkflowStageOutDto[];
  localIdToServerId: Map<string, string>;
};

export class WorkflowStageNameError extends Error {
  readonly emptyNameLocalIds: string[];

  constructor(emptyNameLocalIds: string[]) {
    super(`Stage name is required. Empty stage local ids: ${emptyNameLocalIds.join(', ')}`);
    this.name = 'WorkflowStageNameError';
    this.emptyNameLocalIds = emptyNameLocalIds;
  }
}

export function isWorkflowStageNameError(
  error: unknown
): error is WorkflowStageNameError {
  return error instanceof WorkflowStageNameError;
}

export async function runWorkflowSave(params: RunWorkflowSaveParams): Promise<void> {
  const ctx = createWorkflowSaveContext(params);

  validateMinStageCount(ctx);
  validateStageNames(ctx);

  await runPhase('updateWorkflow', () => updateWorkflow(ctx));
  await runPhase('updateStages', () => updateStages(ctx));
  await runPhase('createStages', () => createStages(ctx));
  await runPhase('deleteStages', () => deleteStages(ctx));

  if (ctx.diff.orderChanged) {
    await runPhase('reorderStages', () => reorderStages(ctx));
  }
}

// Intentionally validates only stages being created or having their name explicitly
// updated (name present in the patch). Persisted stages whose name is not being
// changed are not re-validated here — the UI `canPersist` guard already prevents
// saving when any local stage row has an empty name, covering that path.
function validateStageNames(ctx: WorkflowSaveContext): void {
  const emptyIds: string[] = [];

  for (const op of ctx.diff.toCreate) {
    if (!op.createBody.name || op.createBody.name.trim().length === 0) {
      emptyIds.push(op.localId);
    }
  }

  for (const op of ctx.diff.toUpdate) {
    if (op.dto.name !== undefined && op.dto.name.trim().length === 0) {
      emptyIds.push(op.serverId);
    }
  }

  if (emptyIds.length > 0) {
    throw new WorkflowStageNameError(emptyIds);
  }
}

function validateMinStageCount(ctx: WorkflowSaveContext): void {
  const finalCount = ctx.diff.desiredOrderLocalIds.length;
  if (finalCount < 2) {
    throw new Error(
      `Refusing save: backend requires at least 2 stages, the draft has ${String(finalCount)}.`
    );
  }
}

function createWorkflowSaveContext(params: RunWorkflowSaveParams): WorkflowSaveContext {
  return {
    ...params,
    localIdToServerId: new Map(params.diff.persistedServerIdByLocalId),
  };
}

async function runPhase<T>(phase: WorkflowSavePhase, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw new WorkflowSavePhaseError(phase, error);
  }
}

async function updateWorkflow(ctx: WorkflowSaveContext): Promise<void> {
  if (!ctx.diff.workflowNameUpdate) {
    return;
  }
  await ctx.executor.updateWorkflow(ctx.workflowId, ctx.diff.workflowNameUpdate, ctx.projectId);
}

async function deleteStages(ctx: WorkflowSaveContext): Promise<void> {
  const existingStageIds = new Set(ctx.getStages().map((stage) => stage._id));
  const stagesToDelete = ctx.diff.toDelete.filter((stageId) => existingStageIds.has(stageId));

  for (const stageId of stagesToDelete) {
    await ignoreNotFound(() => ctx.executor.deleteStage(ctx.workflowId, stageId, ctx.projectId));
  }
}

async function ignoreNotFound(fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn();
  } catch (error) {
    if (isNotFoundError(error)) {
      return;
    }
    throw error;
  }
}

function isNotFoundError(error: unknown): boolean {
  const err = (error ?? {}) as { status?: number; statusCode?: number; message?: string; details?: string };
  if (err.status === 404 || err.statusCode === 404) {
    return true;
  }
  const message = String(err.message ?? err.details ?? error ?? '').toLowerCase();
  return message.includes('404') || message.includes('not found') || message.includes('does not exist');
}

async function updateStages(ctx: WorkflowSaveContext): Promise<void> {
  for (const op of ctx.diff.toUpdate) {
    await ignoreNotFound(() => ctx.executor.updateStage(ctx.workflowId, op.serverId, op.dto, ctx.projectId));
  }
}

async function createStages(ctx: WorkflowSaveContext): Promise<void> {
  const eligibleAnchors = new Set(ctx.diff.baselineServerStageIds);

  for (const op of ctx.diff.toCreate) {
    const orderHints = pickSingleAnchor(
      ctx.diff.desiredOrderLocalIds,
      op.rowIndex,
      ctx.localIdToServerId,
      eligibleAnchors
    );

    const body: StageCreateInDto = {
      ...op.createBody,
      ...orderHints,
    };

    const createdStage = await ctx.executor.createStage(ctx.workflowId, body, ctx.projectId);

    await patchMissingStageFields(ctx, createdStage, op);

    ctx.localIdToServerId.set(op.localId, createdStage._id);
    eligibleAnchors.add(createdStage._id);
  }
}

async function patchMissingStageFields(
  ctx: WorkflowSaveContext,
  createdStage: WorkflowStageOutDto,
  op: WorkflowSaveDiff['toCreate'][number]
): Promise<void> {
  const updateDto: StageUpdateInDto = {};
  if (op.deadlineDays !== undefined && createdStage.deadlineDays !== op.deadlineDays) {
    updateDto.deadlineDays = op.deadlineDays;
  }
  if (op.description !== undefined && createdStage.description !== op.description) {
    updateDto.description = op.description;
  }
  if (Object.keys(updateDto).length === 0) {
    return;
  }

  await ignoreNotFound(() =>
    ctx.executor.updateStage(ctx.workflowId, createdStage._id, updateDto, ctx.projectId)
  );
}

function pickSingleAnchor(
  desiredOrderLocalIds: readonly string[],
  rowIndex: number,
  localIdToServerId: ReadonlyMap<string, string>,
  validSiblingStageIds: ReadonlySet<string>
): { afterId?: string; beforeId?: string } {
  for (let i = rowIndex - 1; i >= 0; i--) {
    const serverId = localIdToServerId.get(desiredOrderLocalIds[i]);
    if (serverId && validSiblingStageIds.has(serverId)) {
      return { afterId: serverId };
    }
  }

  for (let i = rowIndex + 1; i < desiredOrderLocalIds.length; i++) {
    const serverId = localIdToServerId.get(desiredOrderLocalIds[i]);
    if (serverId && validSiblingStageIds.has(serverId)) {
      return { beforeId: serverId };
    }
  }

  return {};
}

async function reorderStages(ctx: WorkflowSaveContext): Promise<void> {
  const desired = ctx.diff.desiredOrderLocalIds
    .map((localId) => ctx.localIdToServerId.get(localId))
    .filter((id): id is string => id !== undefined);

  const current = sortedStagesByFractionalOrder(ctx.getStages()).map((stage) => stage._id);
  const existingDesired = desired.filter((id) => current.includes(id));
  const operations = planReorderOperations(current, existingDesired);

  for (const operation of operations) {
    await ctx.executor.reorderStage(
      ctx.workflowId,
      operation.stageId,
      buildReorderPayload(operation),
      ctx.projectId
    );
  }
}
